
import { format } from 'date-fns';

export const formatDate = (date: Date): string => {
  try {
    return format(date, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return '';
  }
};

// Add a helper to parse date strings safely
export const parseDate = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  try {
    const parsedDate = new Date(dateString);
    return !isNaN(parsedDate.getTime()) ? parsedDate : null;
  } catch (error) {
    console.error('Error parsing date:', error, dateString);
    return null;
  }
};

// Format date for display (with fallback)
export const formatDateWithFallback = (dateString: string | undefined, fallback: string = ''): string => {
  if (!dateString) return fallback;
  
  try {
    const parsedDate = new Date(dateString);
    if (isNaN(parsedDate.getTime())) return fallback;
    
    return format(parsedDate, 'MMM d, yyyy, h:mm:ss.SSS a');
  } catch (error) {
    console.error('Error in formatDateWithFallback:', error, dateString);
    return fallback;
  }
};

// Specifically for Firestore timestamps
export const formatFirestoreTimestamp = (timestamp: any): string => {
  if (!timestamp) return '';
  
  try {
    // Check for serverTimestamp placeholders (they have a _methodName property)
    if (timestamp && timestamp._methodName === 'serverTimestamp') {
      return 'Just now';
    }
    
    // Handle Firestore timestamp objects that have a toDate() method
    if (timestamp && typeof timestamp.toDate === 'function') {
      return format(timestamp.toDate(), 'MMM d, yyyy, h:mm:ss.SSS a');
    }
    
    // Handle date objects
    if (timestamp instanceof Date) {
      return format(timestamp, 'MMM d, yyyy, h:mm:ss.SSS a');
    }
    
    // Handle ISO strings
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return format(date, 'MMM d, yyyy, h:mm:ss.SSS a');
      }
    }
    
    // Handle numeric timestamps
    if (typeof timestamp === 'number') {
      return format(new Date(timestamp), 'MMM d, yyyy, h:mm:ss.SSS a');
    }
    
    // Handle formattedDate strings that are already formatted
    if (typeof timestamp === 'string' && (
      timestamp.includes('AM') || 
      timestamp.includes('PM') || 
      timestamp.includes('am') || 
      timestamp.includes('pm')
    )) {
      return timestamp;
    }
    
    // If it's an object with specific formatted date fields, use those
    if (timestamp && typeof timestamp === 'object') {
      if (timestamp.displayUpdatedAt) return timestamp.displayUpdatedAt;
      if (timestamp.formattedUpdatedAt) {
        const date = new Date(timestamp.formattedUpdatedAt);
        if (!isNaN(date.getTime())) {
          return format(date, 'MMM d, yyyy, h:mm:ss.SSS a');
        }
      }
    }
    
    return String(timestamp);
  } catch (error) {
    console.error('Error formatting Firestore timestamp:', error, timestamp);
    return String(timestamp);
  }
};

// Convert any timestamp format to a JavaScript Date object
export const timestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  
  try {
    // Check for serverTimestamp placeholders
    if (timestamp && timestamp._methodName === 'serverTimestamp') {
      return new Date(); // Return current date as an approximation
    }
    
    // Handle Firestore timestamp objects that have a toDate() method
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // Handle date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Handle ISO strings
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    // Handle numeric timestamps
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // Handle objects with formatted date fields
    if (timestamp && typeof timestamp === 'object') {
      if (timestamp.formattedUpdatedAt) {
        const date = new Date(timestamp.formattedUpdatedAt);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      if (timestamp.formattedCreatedAt) {
        const date = new Date(timestamp.formattedCreatedAt);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error converting timestamp to Date:', error, timestamp);
    return null;
  }
};
